import { Dispatch, SetStateAction, useEffect } from 'react';
import Button from 'src/components/primitives/Button/Button';
import { WithChildren, WithClassName } from 'src/utils/UtilityTypes';
import ResponsiveModal from '../ResponsiveModal/ResponsiveModal';
import PresetTag from 'src/components/Tags/PresetTag';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { FeatureAnnouncementModalPageConfig } from 'src/components/Modals/FeatureAnnouncementModal/types';
import {
	FEATURE_ANNOUNCEMENTS,
	FeatureAnnouncementName,
} from 'src/constants/featureAnnouncements';
import { cj } from 'src/utils/classNames';

interface Props extends WithChildren, WithClassName {
	title: string;
	onClose: () => void;
	onBack?: () => void;
	onNext?: () => void;
	/**
	 * The name of the announcement. Used to track the last seen announcement.
	 */
	announcementName: FeatureAnnouncementName;
	/**
	 * Note this should be zero-indexed.
	 */
	currentStep: number;
	setCurrentStep: Dispatch<SetStateAction<number>>;
	showBackButton?: boolean;
	showNextButton?: boolean;
	nextButtonText?: string;
	backButtonText?: string;
	finalStepButtonText?: string;
	bodyClassName?: string;
	pagesConfig: FeatureAnnouncementModalPageConfig[];
}

export default function FeatureAnnouncementBaseModal({
	title,
	onClose,
	onBack,
	onNext,
	currentStep,
	setCurrentStep,
	showBackButton = false,
	showNextButton = true,
	nextButtonText = 'Next',
	backButtonText = 'Back',
	finalStepButtonText = 'Finish',
	children,
	className,
	bodyClassName,
	announcementName,
	pagesConfig,
}: Props) {
	const isMobileScreenSize = useIsMobileScreenSize();

	const totalSteps = pagesConfig.length;
	const isFinalStep = currentStep === totalSteps - 1;

	const isCurrentAnnouncement =
		FEATURE_ANNOUNCEMENTS[announcementName].isCurrent;

	// Preload media.
	useEffect(() => {
		pagesConfig.forEach((page) => {
			const desktopMedia = page.desktopMedia;
			const mobileMedia = page.mobileMedia;

			[isMobileScreenSize ? mobileMedia : desktopMedia].forEach((media) => {
				if (media.type === 'image') {
					const img = new Image();
					img.src = media.src;
				} else if (media.type === 'video') {
					const video = document.createElement('video');
					video.src = media.src;
					video.preload = 'auto';
					video.load();
				}
			});
		});
	}, [isMobileScreenSize]);

	const handleNext = () => {
		setCurrentStep((prev) => prev + 1);

		onNext?.();
	};

	const handleBack = () => {
		setCurrentStep((prev) => prev - 1);

		onBack?.();
	};

	return (
		<ResponsiveModal.Container
			onClose={onClose}
			className={cj(
				'not-sm:h-[95dvh] not-sm:max-h-[95dvh] sm:w-[632px]',
				className
			)}
			containerClassName="backdrop-blur-xs sm:backdrop-blur-none"
		>
			<ResponsiveModal.Title>
				{title}
				{isCurrentAnnouncement && <PresetTag preset="new" />}
			</ResponsiveModal.Title>

			<ResponsiveModal.Body className={bodyClassName}>
				{children}
			</ResponsiveModal.Body>

			{(showBackButton || showNextButton || isFinalStep) && (
				<ResponsiveModal.Footer className="items-center justify-end">
					{({ close }) => (
						<>
							{showBackButton && (
								<Button size="md" variant="secondary" onClick={handleBack}>
									{backButtonText}
								</Button>
							)}

							{showNextButton && !isFinalStep && (
								<Button size="md" onClick={handleNext}>
									{nextButtonText}
								</Button>
							)}

							{isFinalStep && (
								<Button size="md" onClick={close}>
									{finalStepButtonText}
								</Button>
							)}
						</>
					)}
				</ResponsiveModal.Footer>
			)}
		</ResponsiveModal.Container>
	);
}
